import { ThumbsDown, ThumbsUp } from 'lucide-react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

import type { ChatFeedbackRating } from '../hooks/useChatFeedback';

interface ChatFeedbackProps {
  turnId: string;
  rating: ChatFeedbackRating | null;
  canRate: boolean;
  busy?: boolean;
  onRate: (rating: ChatFeedbackRating | null) => void;
}

const SHARED_TOOLTIP = 'Only the chat owner can rate answers';

function ThumbButton({
  label,
  pressed,
  disabled,
  busy,
  onClick,
  Icon,
  selectedClass,
}: {
  label: string;
  pressed: boolean;
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
  Icon: typeof ThumbsUp;
  selectedClass: string;
}): React.ReactElement {
  return (
    <Button
      type="button"
      variant="tertiary"
      size="sm"
      iconOnly
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      aria-disabled={disabled || busy}
      onClick={onClick}
      Icon={<Icon className={cn(pressed ? ['fill-current', selectedClass] : 'text-muted-foreground')} />}
      className={cn(pressed && selectedClass, busy && 'cursor-not-allowed opacity-50')}
    />
  );
}

/**
 * One thumbs-up / thumbs-down pair for a completed answer. Clicking the selected thumb clears the
 * vote; clicking the other thumb switches it.
 */
export function ChatFeedback({ turnId, rating, canRate, busy = false, onRate }: ChatFeedbackProps): React.ReactElement {
  const disabled = !canRate;
  const tooltip = !canRate ? SHARED_TOOLTIP : busy ? 'Saving feedback…' : null;

  const handle = (next: ChatFeedbackRating): void => {
    if (disabled || busy) return;
    onRate(rating === next ? null : next);
  };

  const up = (
    <ThumbButton
      label="Thumbs up"
      pressed={rating === 'up'}
      disabled={disabled}
      busy={busy}
      onClick={() => handle('up')}
      Icon={ThumbsUp}
      selectedClass="text-primary"
    />
  );
  const down = (
    <ThumbButton
      label="Thumbs down"
      pressed={rating === 'down'}
      disabled={disabled}
      busy={busy}
      onClick={() => handle('down')}
      Icon={ThumbsDown}
      selectedClass="text-destructive"
    />
  );

  return (
    <div
      className="mx-6 my-1 flex items-center gap-0.5"
      data-testid="chat-feedback"
      data-turn-id={turnId}
      role="group"
      aria-label="Rate this answer"
    >
      <ActionTooltip keepMounted focusableTrigger={disabled} show={Boolean(tooltip)} message={tooltip ?? ''}>
        {up}
      </ActionTooltip>
      <ActionTooltip keepMounted focusableTrigger={disabled} show={Boolean(tooltip)} message={tooltip ?? ''}>
        {down}
      </ActionTooltip>
    </div>
  );
}
